/**
 * This plugin declares key handlers specific to screenplay and variety scripts
 */

import {
  chainCommands,
  joinDown,
  joinUp,
  lift,
  toggleMark,
} from 'prosemirror-commands'
import { undoInputRule } from 'prosemirror-inputrules'
import { keymap } from 'prosemirror-keymap'
import { MarkType } from 'prosemirror-model'
import { Command } from 'prosemirror-state'

import { schema } from '@showrunner/prose-schemas'

import {
  backspacePreservePageBreak,
  handleEnter,
  removePageBreak,
  toggleCaps,
} from '#util'

import { PluginFactory } from '../types'

import { insertHardBreak } from './commands'
import {
  lockedPageBackspace,
  lockedPageDelete,
  lockedPageEnter,
} from './locked-page-commands'
import { selectAlmostAll } from './select-almost-all'
import { getTabHandler } from './tab'

type KeyCombo = string

/**
 * Define keyboard shortcuts specific to toggling marks on and off
 */
export const MARK_SHORTCUTS = new Map<MarkType, KeyCombo>([
  [schema.marks.strong, 'Mod-b'],
  [schema.marks.em, 'Mod-i'],
  [schema.marks.underline, 'Mod-u'],
  [schema.marks.strike, 'Shift-Mod-u'],
])

/**
 * Create a plugin to handle editor-specific key bindings.
 * Must be declared before other keymaps to intercept user actions first.
 * @param {object} config - plugin config
 * @param {object} config.script - script object from backend
 * @param {string} config.script.type - script doc type
 */
export const editorKeymapPlugin: PluginFactory = ({ script }) => {
  const { type } = script
  const handleTab = getTabHandler(type)

  const keys: Record<KeyCombo, Command> = {
    // fun fact: on a mac the delete key is actually backspace (unless you hold down fn)
    // note that if neither of these commands fire, the base-keymap will handle (for deleting
    // the selection, etc.
    Backspace: chainCommands(
      lockedPageBackspace,
      removePageBreak,
      backspacePreservePageBreak,
      undoInputRule,
    ),
    Delete: lockedPageDelete,
    'Mod-Backspace': lockedPageBackspace,
    // this isn't a 'real' key combo but CJ and Amy like to think it is
    'Shift-Backspace': lockedPageBackspace,
    'Alt-ArrowUp': joinUp,
    'Alt-ArrowDown': joinDown,
    'Mod-BracketLeft': lift,
    // Enter & Friends
    Enter: chainCommands(lockedPageEnter, handleEnter),
    'Shift-Enter': insertHardBreak,
    // NOTE: all keydown handlers below are overriding browser defaults.
    // this may prove to be a bad idea! so says nate.
    // TAB!!!
    // NOTE: tab and shift+tab are used for accessibility
    Tab: handleTab(false),
    'Shift-Tab': handleTab(true),
    'Mod-a': selectAlmostAll,
    'Mod-Alt-a': toggleCaps,
  }

  for (const [mark, keyCombo] of MARK_SHORTCUTS) {
    keys[keyCombo] = toggleMark(mark, null, { removeWhenPresent: false })
  }

  return keymap(keys)
}
